import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiTag } from 'react-icons/fi';
import { getClientCustomPricingDiscountCatalog } from '../api';

const formatMoney = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;

const toDiscountMap = (discounts = []) => {
  const map = new Map();
  (Array.isArray(discounts) ? discounts : []).forEach((discount) => {
    const varietyPriceId = Number(discount?.varietyPriceId);
    if (!Number.isInteger(varietyPriceId)) return;
    map.set(varietyPriceId, {
      varietyPriceId,
      discountType: discount.discountType || 'amount',
      discountValue: discount.discountValue ?? '',
    });
  });
  return map;
};

const mapToDiscountList = (map) => Array.from(map.values());

const calculateDiscountAmount = ({ basePrice, discountType, discountValue }) => {
  const price = Math.max(0, Math.floor(Number(basePrice || 0)));
  const value = Math.max(0, Number(discountValue || 0));
  const raw = discountType === 'percent'
    ? Math.floor(price * (value / 100))
    : Math.floor(value);
  return Math.min(Math.max(raw, 0), price);
};

const ClientPricingDiscountConfigurator = ({
  mode = 'none',
  onModeChange,
  categoryValue = '',
  onCategoryChange,
  categories = [],
  categoriesLoading = false,
  customDiscounts = [],
  onCustomDiscountsChange,
  saveAsCategory = false,
  onSaveAsCategoryChange,
  categoryName = '',
  onCategoryNameChange,
  categoryDescription = '',
  onCategoryDescriptionChange,
  clientId = null,
  disabled = false,
  className = '',
}) => {
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [rowsError, setRowsError] = useState('');
  const [search, setSearch] = useState('');
  const [bulkForm, setBulkForm] = useState({ discountType: 'amount', discountValue: '' });
  const [draftReady, setDraftReady] = useState(false);
  const initialLoadRef = useRef(false);
  const customDiscountsRef = useRef(customDiscounts);
  const modeRef = useRef(mode);
  const categoryValueRef = useRef(categoryValue);
  const onModeChangeRef = useRef(onModeChange);
  const onCustomDiscountsChangeRef = useRef(onCustomDiscountsChange);

  useEffect(() => {
    customDiscountsRef.current = customDiscounts;
  }, [customDiscounts]);

  useEffect(() => {
    modeRef.current = mode;
    categoryValueRef.current = categoryValue;
    onModeChangeRef.current = onModeChange;
    onCustomDiscountsChangeRef.current = onCustomDiscountsChange;
  }, [categoryValue, mode, onCustomDiscountsChange, onModeChange]);

  useEffect(() => {
    initialLoadRef.current = false;
    setDraftReady(false);
  }, [clientId]);

  useEffect(() => {
    let mounted = true;
    const loadRows = async () => {
      if (mode !== 'custom' && !clientId) {
        setRows([]);
        setRowsError('');
        setDraftReady(true);
        return;
      }
      setLoadingRows(true);
      setRowsError('');
      try {
        const data = await getClientCustomPricingDiscountCatalog({
          ...(clientId ? { clientId } : {}),
          ...(search.trim() ? { search: search.trim() } : {}),
        });
        if (!mounted) return;
        const nextRows = Array.isArray(data?.rows) ? data.rows : [];
        setRows(nextRows);
        if (!initialLoadRef.current) {
          const serverDiscounts = nextRows
            .filter((row) => Number(row.discountValue || 0) > 0)
            .map((row) => ({
              varietyPriceId: row.varietyPriceId,
              discountType: row.discountType || 'amount',
              discountValue: row.discountValue || 0,
            }));
          if (serverDiscounts.length > 0) {
            const merged = toDiscountMap(customDiscountsRef.current);
            serverDiscounts.forEach((discount) => {
              if (!merged.has(Number(discount.varietyPriceId))) {
                merged.set(Number(discount.varietyPriceId), discount);
              }
            });
            onCustomDiscountsChangeRef.current?.(mapToDiscountList(merged));
            if (modeRef.current === 'none' && !categoryValueRef.current) {
              onModeChangeRef.current?.('custom');
            }
          }
          initialLoadRef.current = true;
          setDraftReady(true);
        }
      } catch (err) {
        if (!mounted) return;
        setRows([]);
        setRowsError(err?.response?.data?.message || 'No se pudieron cargar los descuentos personalizados.');
      } finally {
        if (mounted) setLoadingRows(false);
      }
    };
    loadRows();
    return () => {
      mounted = false;
    };
  }, [clientId, mode, search]);

  const selectedCategory = categories.find((category) => String(category.id) === String(categoryValue));
  const customDiscountMap = useMemo(() => toDiscountMap(customDiscounts), [customDiscounts]);

  const visibleRows = useMemo(() => rows.map((row) => {
    const draft = customDiscountMap.get(Number(row.varietyPriceId));
    const useServerDiscount = !draftReady && Number(row.discountValue || 0) > 0;
    const discountType = draft?.discountType || (useServerDiscount ? row.discountType : 'amount');
    const discountValue = draft ? draft.discountValue : useServerDiscount ? row.discountValue : 0;
    const discountAmount = calculateDiscountAmount({
      basePrice: row.basePrice,
      discountType,
      discountValue,
    });
    return {
      ...row,
      discountType,
      discountValue,
      discountAmount,
      finalPrice: Math.max(Number(row.basePrice || 0) - discountAmount, 0),
      hasDiscount: discountAmount > 0,
    };
  }), [customDiscountMap, draftReady, rows]);

  const updateDiscount = (varietyPriceId, patch) => {
    const id = Number(varietyPriceId);
    if (!Number.isInteger(id)) return;
    const nextMap = toDiscountMap(customDiscountsRef.current);
    const current = nextMap.get(id) || { varietyPriceId: id, discountType: 'amount', discountValue: '' };
    const next = { ...current, ...patch, varietyPriceId: id };
    if (Number(next.discountValue || 0) > 0) {
      nextMap.set(id, next);
    } else {
      nextMap.delete(id);
    }
    const nextList = mapToDiscountList(nextMap);
    customDiscountsRef.current = nextList;
    onCustomDiscountsChange?.(nextList);
  };

  const applyBulk = (action) => {
    if (action !== 'clear' && !(Number(bulkForm.discountValue) > 0)) {
      setRowsError('Ingresa un descuento mayor a 0.');
      return;
    }
    const nextMap = toDiscountMap(customDiscountsRef.current);
    visibleRows.forEach((row) => {
      const id = Number(row.varietyPriceId);
      if (!Number.isInteger(id)) return;
      if (action === 'clear') {
        nextMap.delete(id);
        return;
      }
      nextMap.set(id, {
        varietyPriceId: id,
        discountType: bulkForm.discountType || 'amount',
        discountValue: bulkForm.discountValue,
      });
    });
    const nextList = mapToDiscountList(nextMap);
    customDiscountsRef.current = nextList;
    onCustomDiscountsChange?.(nextList);
    setRowsError('');
  };

  const activeDiscountCount = customDiscounts.filter((discount) => Number(discount.discountValue || 0) > 0).length;
  const statusLabel = mode === 'category'
    ? selectedCategory ? 'Con categoria' : 'Seleccionar categoria'
    : mode === 'custom'
      ? activeDiscountCount > 0 ? `${activeDiscountCount} descuentos` : 'Personalizado'
      : 'Sin descuento';

  return (
    <section className={`rounded-lg border border-blue-100 bg-blue-50/50 p-4 ${className}`.trim()}>
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-blue-100 text-blue-700">
          <FiTag />
        </div>
        <div className="min-w-0 flex-1 space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <label className="text-xs font-semibold uppercase text-gray-600">
                Descuentos de cliente
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Aplica descuentos automaticos sobre precios mayorista en ventas, cotizaciones y portal.
              </p>
            </div>
            <span className={`w-fit rounded-full px-2 py-1 text-[11px] font-semibold ${
              mode === 'none'
                ? 'bg-gray-100 text-gray-600'
                : 'bg-emerald-100 text-emerald-700'
            }`}
            >
              {statusLabel}
            </span>
          </div>

          <div className="grid gap-2 sm:grid-cols-3">
            {[
              { value: 'none', label: 'Sin descuento' },
              { value: 'category', label: 'Categoria' },
              { value: 'custom', label: 'Personalizado' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onModeChange?.(option.value)}
                disabled={disabled}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                  mode === option.value
                    ? 'border-blue-400 bg-white text-blue-700 ring-2 ring-blue-100'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-blue-200'
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {mode === 'category' && (
            <div className="space-y-3">
              <select
                name="clientPricingCategoryId"
                value={categoryValue || ''}
                onChange={onCategoryChange}
                className="w-full rounded-lg border border-gray-300 bg-white p-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                disabled={disabled || categoriesLoading}
              >
                <option value="">{categoriesLoading ? 'Cargando categorias...' : 'Seleccionar categoria'}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {selectedCategory?.description && (
                <p className="rounded border border-blue-100 bg-white px-3 py-2 text-xs text-gray-600">
                  {selectedCategory.description}
                </p>
              )}
              {!categoriesLoading && categories.length === 0 && (
                <p className="rounded border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  No hay categorias activas. Crealas en Configuracion, Clientes.
                </p>
              )}
            </div>
          )}

          {mode === 'custom' && (
            <div className="space-y-4">
              <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={Boolean(saveAsCategory)}
                  onChange={(event) => onSaveAsCategoryChange?.(event.target.checked)}
                  disabled={disabled}
                />
                Convertir en categoria al guardar
              </label>

              {saveAsCategory && (
                <div className="grid gap-3 rounded-lg border border-blue-100 bg-white p-3 md:grid-cols-2">
                  <input
                    type="text"
                    value={categoryName}
                    onChange={(event) => onCategoryNameChange?.(event.target.value)}
                    placeholder="Nombre de categoria"
                    className="h-10 rounded border border-gray-300 px-3 text-sm"
                    disabled={disabled}
                  />
                  <input
                    type="text"
                    value={categoryDescription}
                    onChange={(event) => onCategoryDescriptionChange?.(event.target.value)}
                    placeholder="Descripcion opcional"
                    className="h-10 rounded border border-gray-300 px-3 text-sm"
                    disabled={disabled}
                  />
                </div>
              )}

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Herramientas masivas</div>
                    <p className="text-xs text-gray-500">
                      Aplica o limpia descuentos en las filas mayorista visibles.
                    </p>
                  </div>
                  {loadingRows && <span className="text-xs font-semibold text-blue-700">Cargando precios...</span>}
                </div>
                <div className="grid min-w-0 gap-2 lg:grid-cols-[minmax(0,1fr),auto]">
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar producto, variedad o formato"
                    className="h-10 w-full min-w-0 rounded border border-gray-300 px-3 text-sm"
                    disabled={disabled}
                  />
                  <div className="flex min-w-0 flex-wrap gap-2">
                    <select
                      value={bulkForm.discountType}
                      onChange={(event) => setBulkForm((prev) => ({ ...prev, discountType: event.target.value }))}
                      className="h-10 rounded border border-gray-300 px-2 text-sm"
                      disabled={disabled}
                    >
                      <option value="amount">Monto</option>
                      <option value="percent">%</option>
                    </select>
                    <input
                      type="number"
                      min="0"
                      value={bulkForm.discountValue}
                      onChange={(event) => setBulkForm((prev) => ({ ...prev, discountValue: event.target.value }))}
                      placeholder={bulkForm.discountType === 'percent' ? '10' : '2000'}
                      className="h-10 w-24 rounded border border-gray-300 px-2 text-sm"
                      disabled={disabled}
                    />
                    <button
                      type="button"
                      onClick={() => applyBulk('apply')}
                      disabled={disabled || loadingRows}
                      className="rounded bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-60"
                    >
                      Aplicar visibles
                    </button>
                    <button
                      type="button"
                      onClick={() => applyBulk('clear')}
                      disabled={disabled || loadingRows}
                      className="rounded border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
                    >
                      Limpiar visibles
                    </button>
                  </div>
                </div>
                {rowsError && (
                  <p className="mt-3 rounded border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
                    {rowsError}
                  </p>
                )}
              </div>

              <div className="max-w-full overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-[780px] w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Producto</th>
                      <th className="px-3 py-2 text-left">Formato</th>
                      <th className="px-3 py-2 text-right">Base</th>
                      <th className="px-3 py-2 text-left">Descuento</th>
                      <th className="px-3 py-2 text-right">Final</th>
                      <th className="px-3 py-2 text-right">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {visibleRows.map((row) => (
                      <tr key={row.varietyPriceId}>
                        <td className="px-3 py-2">
                          <div className="break-words font-medium text-gray-900">{row.productName}</div>
                          <div className="break-words text-xs text-gray-500">{row.varietyName}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {[row.metricUnit, row.description].filter(Boolean).join(' - ') || 'Mayorista'}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-gray-900">
                          {formatMoney(row.basePrice)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex min-w-[190px] items-center gap-2">
                            <select
                              value={row.discountType || 'amount'}
                              onChange={(event) => updateDiscount(row.varietyPriceId, { discountType: event.target.value })}
                              className="h-9 rounded border border-gray-300 px-2 text-sm"
                              disabled={disabled}
                            >
                              <option value="amount">Monto</option>
                              <option value="percent">%</option>
                            </select>
                            <input
                              type="number"
                              min="0"
                              value={row.discountValue ?? ''}
                              onChange={(event) => updateDiscount(row.varietyPriceId, { discountValue: event.target.value })}
                              className="h-9 w-24 rounded border border-gray-300 px-2 text-sm"
                              disabled={disabled}
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {row.discountAmount > 0 ? `-${formatMoney(row.discountAmount)}` : 'Sin descuento'}
                          </p>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-green-700">
                          {formatMoney(row.finalPrice)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                            row.hasDiscount
                              ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                              : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200'
                          }`}
                          >
                            {row.hasDiscount ? 'Borrador' : 'Sin descuento'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {!loadingRows && visibleRows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-500">
                          No hay precios mayorista visibles para este filtro.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ClientPricingDiscountConfigurator;
